import React, { useState, useEffect, useRef, useCallback } from 'react';
import VideoService from '../services/VideoService';
import VideoControls from './VideoControls';
import SessionTimer from './SessionTimer';

const VideoRoom = ({ roomName, identity, onSessionEnd, partnerInfo }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [localVideoElement, setLocalVideoElement] = useState(null);
  const [remoteVideoElement, setRemoteVideoElement] = useState(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(300); // 5 minutes
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [initError, setInitError] = useState(null);
  const [wakeLock, setWakeLock] = useState(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  // Request wake lock to keep screen awake on mobile
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        const lock = await navigator.wakeLock.request('screen');
        setWakeLock(lock);
        console.log('🔒 Wake lock acquired - screen will stay awake');
        
        lock.addEventListener('release', () => {
          console.log('🔓 Wake lock released');
        });
      } else {
        console.log('⚠️ Wake Lock API not supported');
      }
    } catch (err) {
      console.error('❌ Wake lock error:', err);
    }
  };
  const sessionTimerRef = useRef(null);

  // Initialize video connection
  useEffect(() => {
    initializeVideoConnection();
    
    return () => {
      VideoService.disconnect();
    };
  }, [roomName, identity]);

  const initializeVideoConnection = async () => {
    try {
      console.log('🎬 VideoRoom: Starting initialization...');
      setConnectionStatus('initializing');
      
      // Initialize local tracks
      console.log('📹 VideoRoom: Requesting local tracks...');
      const localTracks = await VideoService.initialize();
      console.log('✅ VideoRoom: Local tracks obtained:', localTracks.length);
      
      // Attach local video track to DOM
      const localVideoTrack = localTracks.find(track => track.kind === 'video');
      if (localVideoTrack && localVideoRef.current) {
        console.log('📺 VideoRoom: Attaching local video to DOM...');
        const videoElement = localVideoTrack.attach();
        
        // Add mobile-friendly attributes
        videoElement.setAttribute('playsinline', 'true');
        videoElement.setAttribute('autoplay', 'true');
        videoElement.setAttribute('muted', 'true');
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        // NOTE: Removed scaleX(-1) transform - causes iOS Safari WebRTC rendering bug (black screens)
        console.log('📱 Mobile attributes added to local video');
        
        localVideoRef.current.appendChild(videoElement);
        setLocalVideoElement(videoElement);
        console.log('✅ VideoRoom: Local video attached');
      }

      setConnectionStatus('connecting');
      console.log('🔗 VideoRoom: Connecting to room:', roomName);
      
      // Connect to room
      const room = await VideoService.connectToRoom(roomName, identity);
      console.log('✅ VideoRoom: Connected to Twilio room');
      
      // Check for existing participants (users who joined before us)
      console.log('👥 Checking for existing participants...');
      const existingParticipants = Array.from(room.participants.values());
      console.log('👥 Found', existingParticipants.length, 'existing participants');
      
      if (existingParticipants.length > 0) {
        setParticipants(existingParticipants);
        
        // Subscribe to their tracks
        existingParticipants.forEach(participant => {
          console.log('👤 Existing participant:', participant.identity);
          console.log('📊 Participant tracks:', participant.tracks.size);
          
          participant.tracks.forEach(publication => {
            console.log('📋 Track publication:', {
              kind: publication.kind,
              isSubscribed: publication.isSubscribed,
              isEnabled: publication.isEnabled,
              trackSid: publication.trackSid
            });
            
            if (publication.isSubscribed) {
              const track = publication.track;
              
              // Handle video track
              if (track && track.kind === 'video' && remoteVideoRef.current) {
                console.log('📺 Attaching existing participant video');
                console.log('📊 Video track:', {
                  enabled: track.isEnabled,
                  started: track.isStarted,
                  dimensions: track.dimensions
                });
                const videoElement = track.attach();
                console.log('📺 Video element:', videoElement);
                
                // Add mobile-friendly attributes
                videoElement.setAttribute('playsinline', 'true');
                videoElement.setAttribute('autoplay', 'true');
                videoElement.setAttribute('muted', 'false');
                videoElement.style.width = '100%';
                videoElement.style.height = '100%';
                videoElement.style.objectFit = 'cover';
                console.log('📱 Mobile attributes added to existing participant video');
                
                remoteVideoRef.current.innerHTML = '';
                remoteVideoRef.current.appendChild(videoElement);
                setRemoteVideoElement(videoElement);
                
                // Force play on mobile
                videoElement.play().then(() => {
                  console.log('✅ Existing participant video playing');
                }).catch(err => {
                  console.error('❌ Existing participant video play failed:', err);
                });
                
                console.log('✅ Existing participant video attached');
              }
              
              // Handle audio track
              if (track && track.kind === 'audio') {
                console.log('🔊 Attaching existing participant audio');
                const audioElement = track.attach();
                audioElement.setAttribute('autoplay', 'true');
                document.body.appendChild(audioElement);
                console.log('✅ Existing participant audio attached');
              }
            } else {
              console.log('⚠️ Track not subscribed yet, waiting...');
            }
          });
        });
      }
      
      setIsConnected(true);
      setConnectionStatus('connected');
      reconnectAttempts.current = 0; // Reset reconnect counter on successful connection
      
      // Request wake lock for mobile devices
      await requestWakeLock();
      
      startSessionTimer();
      
    } catch (error) {
      console.error('❌ VideoRoom initialization failed:', error);
      console.error('Error details:', error.message, error.code);
      setConnectionStatus('failed');
      setInitError(error.message || 'Failed to initialize video');
    }
  };

  // Handle page visibility changes (mobile tab switching, background)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('📱 Page became visible - reacquiring wake lock');
        if (!wakeLock || wakeLock.released) {
          await requestWakeLock();
        }
      } else {
        console.log('📱 Page hidden - wake lock may release');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [wakeLock]);
  // Setup event listeners
  useEffect(() => {
    const handleParticipantConnected = (participant) => {
      setParticipants(prev => [...prev, participant]);
    };

    const handleParticipantDisconnected = (participant) => {
      setParticipants(prev => prev.filter(p => p.sid !== participant.sid));
    };

    const handleTrackSubscribed = ({ track, participant }) => {
      console.log('🎬 Track subscribed:', track.kind, 'from', participant.identity);
      console.log('📊 Track details:', {
        kind: track.kind,
        enabled: track.isEnabled,
        started: track.isStarted,
        dimensions: track.dimensions
      });
      
      if (track.kind === 'video' && remoteVideoRef.current) {
        console.log('📺 Attaching remote video track to DOM...');
        const videoElement = track.attach();
        console.log('📺 Video element created:', videoElement);
        
        // Add mobile-friendly attributes
        videoElement.setAttribute('playsinline', 'true');
        videoElement.setAttribute('autoplay', 'true');
        videoElement.setAttribute('muted', 'false');
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        console.log('📱 Mobile attributes added to remote video');
        
        remoteVideoRef.current.innerHTML = '';
        remoteVideoRef.current.appendChild(videoElement);
        setRemoteVideoElement(videoElement);
        
        // Force play on mobile
        videoElement.play().then(() => {
          console.log('✅ Remote video playing');
        }).catch(err => {
          console.error('❌ Remote video play failed:', err);
        });
        
        console.log('✅ Remote video attached successfully');
      } else if (track.kind === 'audio') {
        console.log('🔊 Attaching remote audio track...');
        const audioElement = track.attach();
        audioElement.setAttribute('autoplay', 'true');
        
        // Append to document body (audio doesn't need to be visible)
        document.body.appendChild(audioElement);
        
        console.log('✅ Remote audio attached successfully');
      } else {
        console.log('⚠️ Skip track:', track.kind !== 'video' && track.kind !== 'audio' ? 'unknown type' : 'no ref');
      }
    };

    const handleTrackUnsubscribed = ({ track, participant }) => {
      if (track.kind === 'video' && remoteVideoRef.current) {
        remoteVideoRef.current.innerHTML = '';
        setRemoteVideoElement(null);
      }
    };

    const handleDisconnected = ({ room, error }) => {
      setIsConnected(false);
      setConnectionStatus('disconnected');
      console.log('📡 Disconnected from room');
      
      if (error) {
        console.error('Room disconnected with error:', error);
      }
      
      // Auto-reconnect on mobile
      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        console.log(`🔄 Auto-reconnect attempt ${reconnectAttempts.current}/${maxReconnectAttempts}`);
        setConnectionStatus('reconnecting');
        
        setTimeout(() => {
          console.log('🔄 Attempting to reconnect...');
          initializeVideoConnection();
        }, 2000); // Wait 2 seconds before reconnecting
      } else {
        console.error('❌ Max reconnect attempts reached');
        setInitError('Connection lost. Please refresh to reconnect.');
      }
    };

    // Register event listeners
    VideoService.on('participantConnected', handleParticipantConnected);
    VideoService.on('participantDisconnected', handleParticipantDisconnected);
    VideoService.on('trackSubscribed', handleTrackSubscribed);
    VideoService.on('trackUnsubscribed', handleTrackUnsubscribed);
    VideoService.on('disconnected', handleDisconnected);

    return () => {
      VideoService.off('participantConnected', handleParticipantConnected);
      VideoService.off('participantDisconnected', handleParticipantDisconnected);
      VideoService.off('trackSubscribed', handleTrackSubscribed);
      VideoService.off('trackUnsubscribed', handleTrackUnsubscribed);
      VideoService.off('disconnected', handleDisconnected);
      
      // Release wake lock
      if (wakeLock && !wakeLock.released) {
        wakeLock.release().then(() => {
          console.log('🔓 Wake lock released on unmount');
        });
      }
    };
  }, [wakeLock]);

  // Session timer management
  const startSessionTimer = () => {
    sessionTimerRef.current = setInterval(() => {
      setSessionTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(sessionTimerRef.current);
          // Timer expired - end session and cleanup camera/microphone
          console.log('⏰ Timer reached 0 - ending session and cleaning up');
          endSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const endSession = useCallback(() => {
    console.log('🛑 VideoRoom.endSession() called - cleaning up camera and microphone');
    
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
      console.log('✅ Timer cleared');
    }
    
    console.log('🔌 Disconnecting VideoService...');
    VideoService.disconnect();
    console.log('✅ VideoService disconnected - all tracks should be stopped');
    
    if (onSessionEnd) {
      console.log('📞 Calling parent onSessionEnd callback');
      onSessionEnd();
    }
    
    console.log('✅ VideoRoom.endSession() complete');
  }, [onSessionEnd]);

  // Media control handlers
  const toggleAudioMute = async () => {
    try {
      if (isAudioMuted) {
        await VideoService.unmuteAudio();
      } else {
        await VideoService.muteAudio();
      }
      setIsAudioMuted(!isAudioMuted);
    } catch (error) {
      console.error('Failed to toggle audio mute:', error);
    }
  };

  const toggleVideoMute = async () => {
    try {
      if (isVideoMuted) {
        await VideoService.unmuteVideo();
      } else {
        await VideoService.muteVideo();
      }
      setIsVideoMuted(!isVideoMuted);
    } catch (error) {
      console.error('Failed to toggle video mute:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 p-4">
      {/* Debug Info */}
      <div className="max-w-6xl mx-auto mb-2">
        <div className="bg-black/50 backdrop-blur text-white p-2 rounded text-xs font-mono">
          Connected: {isConnected ? '✅' : '❌'} | 
          Participants: {participants.length} | 
          Local Video: {localVideoElement ? '✅' : '❌'} |
          Remote Video: {remoteVideoElement ? '✅' : '❌'} |
          Room: {roomName.substring(11, 40)}...
        </div>
      </div>
      
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="text-white">
                <h2 className="text-xl font-bold">Speed Date Session</h2>
                <p className="text-sm opacity-80">
                  {partnerInfo ? `With ${partnerInfo.name}` : 'Waiting for partner...'}
                </p>
              </div>
            </div>
            
            <SessionTimer 
              timeRemaining={sessionTimeRemaining} 
              onTimeUp={endSession}
            />
            
            <div className="text-right text-white">
              <div className="text-sm opacity-80">Status</div>
              <div className={`text-sm font-semibold ${
                connectionStatus === 'connected' ? 'text-green-300' : 
                connectionStatus === 'failed' ? 'text-red-300' : 'text-yellow-300'
              }`}>
                {connectionStatus.charAt(0).toUpperCase() + connectionStatus.slice(1)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Grid */}
      <div className="max-w-6xl mx-auto mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Local Video */}
          <div className="relative bg-black rounded-2xl overflow-hidden aspect-video">
            <div ref={localVideoRef} className="w-full h-full" />
            <div className="absolute top-2 right-2 bg-yellow-500 text-black px-2 py-1 rounded text-xs font-bold">
              LOCAL: {localVideoElement ? '✅ ATTACHED' : '❌ NO VIDEO'}
            </div>
            <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              You ({identity})
            </div>
            {isVideoMuted && (
              <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="text-4xl mb-2">📹❌</div>
                  <div>Camera Off</div>
                </div>
              </div>
            )}
          </div>

          {/* Remote Video */}
          <div className="relative bg-black rounded-2xl overflow-hidden aspect-video">
            <div ref={remoteVideoRef} className="w-full h-full" />
            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
              REMOTE: {remoteVideoElement ? '✅ ATTACHED' : '❌ NO VIDEO'}
            </div>
            <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {participants.length > 0 ? participants[0].identity : 'Waiting for partner...'}
            </div>
            {participants.length === 0 && (
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="text-4xl mb-2">⏳</div>
                  <div>Waiting for your speed date partner...</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time's Up Notification */}
      {sessionTimeRemaining === 0 && (
        <div className="max-w-6xl mx-auto mb-4">
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-4 text-center animate-pulse">
            <div className="text-white text-2xl font-bold mb-2">⏰ Time's Up!</div>
            <div className="text-white/90 mb-3">
              Your 5-minute speed date is complete. Feel free to continue chatting or end the call.
            </div>
            <button
              onClick={endSession}
              className="bg-white text-orange-600 px-6 py-2 rounded-full font-bold hover:bg-orange-50 transition"
            >
              End Session
            </button>
          </div>
        </div>
      )}
      
      {/* Controls */}
      <VideoControls
        isAudioMuted={isAudioMuted}
        isVideoMuted={isVideoMuted}
        onToggleAudio={toggleAudioMute}
        onToggleVideo={toggleVideoMute}
        onEndCall={endSession}
        isConnected={isConnected}
      />

      {/* Connection Status Banner */}
      {connectionStatus === 'initializing' && (
        <div className="max-w-6xl mx-auto mt-4">
          <div className="bg-blue-500/20 border border-blue-300 rounded-2xl p-4 text-center">
            <div className="text-white text-xl font-bold mb-2">🔄 Initializing Video...</div>
            <div className="text-white/80">Getting camera and microphone access</div>
          </div>
        </div>
      )}
      
      {(connectionStatus === 'connecting' || connectionStatus === 'reconnecting') && (
        <div className="max-w-6xl mx-auto mt-4">
          <div className="bg-yellow-500/20 border border-yellow-300 rounded-2xl p-4 text-center">
            <div className="text-white text-xl font-bold mb-2">
              {connectionStatus === 'reconnecting' ? '🔄 Reconnecting...' : '⏳ Connecting...'}
            </div>
            <div className="text-white/80">
              {connectionStatus === 'reconnecting' 
                ? `Attempt ${reconnectAttempts.current}/${maxReconnectAttempts}` 
                : `Joining video room: ${roomName}`}
            </div>
          </div>
        </div>
      )}
      
      {/* Connection Error */}
      {connectionStatus === 'failed' && (
        <div className="max-w-6xl mx-auto mt-4">
          <div className="bg-red-500/20 border border-red-300 rounded-2xl p-4 text-center">
            <div className="text-white">
              <h3 className="text-lg font-semibold mb-2">❌ Connection Failed</h3>
              <p className="mb-2 text-red-100 font-bold">{initError || 'Unable to connect to video session'}</p>
              <p className="mb-4 text-sm text-white/80">
                Common causes:<br/>
                • Camera/microphone permission denied<br/>
                • Camera in use by another app<br/>
                • Browser doesn't support WebRTC<br/>
                • Network connectivity issues
              </p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={initializeVideoConnection}
                  className="bg-white text-red-500 px-6 py-2 rounded-full font-semibold hover:bg-red-50 transition-colors"
                >
                  🔄 Retry Connection
                </button>
                <button
                  onClick={onSessionEnd}
                  className="bg-white/20 text-white px-6 py-2 rounded-full font-semibold hover:bg-white/30 transition-colors"
                >
                  ← Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoRoom;